export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

export const percent = (value: number | null) => (value === null ? '—' : value.toFixed(1) + '%')

export type Placement =
  | 'topLeft'
  | 'top'
  | 'topRight'
  | 'bottomLeft'
  | 'bottom'
  | 'bottomRight'
  | 'left'
  | 'right'
  | 'rightBottomAligned'
  | 'rightTopAligned'
  | 'leftBottomAligned'
  | 'leftTopAligned'

export type ScreenPosition = {
  top: number
  left: number
}

export const clearTimeoutIfExists = (timeout: NodeJS.Timeout | null | undefined) => {
  if (timeout) clearTimeout(timeout)
}

export const computePortaledComponentPosition = (
  target: HTMLDivElement | null,
  component: HTMLDivElement | null,
  placement: Placement,
  offsetFromTarget: number,
  offsetAlongEdge: number
): ScreenPosition => {
  if (component && target) {
    const targetRect = target.getBoundingClientRect()
    const componentRect = component.getBoundingClientRect()

    let top = 0
    let left = 0

    switch (placement) {
      case 'top':
        top = targetRect.top - componentRect.height - offsetFromTarget
        left = targetRect.left + (targetRect.width - componentRect.width) / 2
        break
      case 'topRight':
        top = targetRect.top - componentRect.height - offsetFromTarget
        left = targetRect.left - offsetAlongEdge
        break
      case 'topLeft':
        top = targetRect.top - componentRect.height - offsetFromTarget
        left = targetRect.right - componentRect.width + offsetAlongEdge
        break
      case 'bottom':
        top = targetRect.bottom + offsetFromTarget
        left = targetRect.left + (targetRect.width - componentRect.width) / 2
        break
      case 'bottomRight':
        top = targetRect.bottom + offsetFromTarget
        left = targetRect.left - offsetAlongEdge
        break
      case 'bottomLeft':
        top = targetRect.bottom + offsetFromTarget
        left = targetRect.right - componentRect.width + offsetAlongEdge
        break
      case 'left':
        top = targetRect.top + (targetRect.height - componentRect.height) / 2
        left = targetRect.left - componentRect.width - offsetFromTarget
        break
      case 'right':
        top = targetRect.top + (targetRect.height - componentRect.height) / 2
        left = targetRect.right + offsetFromTarget
        break
      case 'rightBottomAligned':
        top = targetRect.bottom - componentRect.height + offsetAlongEdge
        left = targetRect.right + offsetFromTarget
        break
      case 'rightTopAligned':
        top = targetRect.top - offsetAlongEdge
        left = targetRect.right + offsetFromTarget
        break
      case 'leftBottomAligned':
        top = targetRect.bottom - componentRect.height + offsetAlongEdge
        left = targetRect.left - componentRect.width - offsetFromTarget
        break
      case 'leftTopAligned':
        top = targetRect.top - offsetAlongEdge
        left = targetRect.left - componentRect.width - offsetFromTarget
    }

    // Adjust for scroll position
    top += window.scrollY
    left += window.scrollX

    return { top, left }
  }
  return { top: 0, left: 0 }
}

export const clearIntervalIfExists = (interval: NodeJS.Timeout | null) => {
  if (interval) clearInterval(interval)
}

export const restartInterval = (
  interval: NodeJS.Timeout | null,
  period: number,
  callback: () => void
): NodeJS.Timeout => {
  clearIntervalIfExists(interval)
  return setInterval(() => callback(), period)
}

export const restartTimeout = (
  timeout: NodeJS.Timeout | null | undefined,
  period: number,
  callback: () => void
): NodeJS.Timeout => {
  clearTimeoutIfExists(timeout)
  return setTimeout(() => callback(), period)
}

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const getTimeoutPromise = (timeout: number) => {
  return new Promise<boolean>((_, reject) => {
    setTimeout(() => reject(new Error()), timeout)
  })
}

export const getDateOneMonthAway = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth() + 1, date.getDate())

export const getIsInternalUser = (email: string): boolean => {
  if (!email) return false
  return email.endsWith('@wispr.ai')
}

interface Logger {
  error: (message: string) => void
}

export const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  functionName: string,
  logger: Logger
): Promise<T | void> => {
  // Wrap async function calls with this to ensure they don't hang forever
  // Caller should check the return value and handle the case where it's void
  let timeoutRef: NodeJS.Timeout | null = null

  const timeout = new Promise<void>((resolve) => {
    timeoutRef = setTimeout(() => {
      logger.error(`Timeout exceeded for function: ${functionName}`)
      resolve()
    }, timeoutMs)
  })

  return Promise.race([promise, timeout]).finally(() => clearTimeoutIfExists(timeoutRef))
}

/**
 * Grab just the first name from a potentially full name
 *
 * @example
 * Jane Doe -> Jane
 * Jane -> Jane
 * '' -> ''
 */
export const extractFirstName = (name: string) => name.split(' ')[0]

/**
 * Generate a team name from a domain by capitalizing the first letter of the second level domain
 * If no second level domain is found, return an empty string
 * If there is a subdomain (i.e. sub.wispr.ai), we don't really know how to generate a team name, so just bail
 *
 * Note: This function exists in the hub and the admin portal. Keep it in sync.
 * TODO: Generate the team name from the signing up users name if sld is not available
 *
 * @example
 * wispr.ai -> Wispr
 */
export const generateTeamNameFromDomain = (domain: string) => {
  const domainParts = domain.split('.')

  // If there is a subdomain bail
  if (domainParts.length > 2) {
    return ''
  }

  // Otherwise, we're confident the first part is the second level domain
  const sld = domainParts[0]
  const sldUppercase = sld.charAt(0).toUpperCase() + sld.slice(1)
  return sld ? sldUppercase : ''
}

export { clsx as c } from 'clsx'
